import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CircularProgress, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import XtramModal, {
    XtramBadge,
    XtramButton,
    XtramCheckbox,
    XtramNotice,
    XtramStat,
    XtramTable,
    xtramStyles,
} from '../Widgets/XtramModal/XtramModal';

const ImportPreviewModal = ({ open, onClose, onConfirm, previewData, loading }) => {
    const { t, i18n } = useTranslation();
    const direction = i18n.dir();
    const [tab, setTab] = useState(0);
    const [attachDocuments, setAttachDocuments] = useState(true);
    const [forceRefreshDocuments, setForceRefreshDocuments] = useState(false);

    const newTransactions = previewData?.transactions || [];
    const duplicateTransactions = previewData?.duplicateTransactions || [];
    const documentsAvailableToAttach =
        previewData?.documentsAvailableToAttach
        ?? duplicateTransactions.filter((r) => r.canAttachDocument).length;
    const documentsAvailableToRefresh =
        previewData?.documentsAvailableToRefresh
        ?? duplicateTransactions.filter((r) => r.canRefreshDocument || r.hasChequeImage).length;
    const hasDuplicateTab =
        !!previewData?.isFinsiteImport
        || duplicateTransactions.length > 0
        || (previewData?.duplicateCount || 0) > 0
        || (previewData?.skippedCount || 0) > 0;
    const newCount = previewData?.transactionsCount || newTransactions.length || 0;
    const isFinsite = !!previewData?.isFinsiteImport;

    useEffect(() => {
        if (open) {
            setTab(0);
            setAttachDocuments(true);
            setForceRefreshDocuments(false);
        }
    }, [open, previewData]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('he-IL', {
            style: 'currency',
            currency: 'ILS',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount ?? 0);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('he-IL');
    };

    const balanceChange = previewData ? previewData.balanceAfter - previewData.balanceBefore : 0;
    const isPositive = balanceChange >= 0;

    const skipReasonLabel = (reason) => {
        if (reason === 'EntryId') return t('FinsiteDuplicateAlreadyImported') || 'Already imported from Finsite';
        if (reason === 'ExistingTransaction') return t('FinsiteDuplicateExistingRow') || 'Matches existing bank row';
        if (reason === 'Suppressed') return t('FinsiteDuplicateSuppressed') || 'Blocked from re-import';
        return t('Duplicate') || 'Duplicate';
    };

    const activeRows = useMemo(() => {
        if (!hasDuplicateTab) return newTransactions;
        return tab === 0 ? newTransactions : duplicateTransactions;
    }, [hasDuplicateTab, tab, newTransactions, duplicateTransactions]);

    const canConfirm = !!previewData
        && !loading
        && (
            newCount > 0
            || (isFinsite && attachDocuments && documentsAvailableToAttach > 0)
            || (isFinsite && forceRefreshDocuments && documentsAvailableToRefresh > 0)
        );

    const confirmLabel = isFinsite
        && newCount === 0
        && (forceRefreshDocuments
            ? documentsAvailableToRefresh > 0
            : documentsAvailableToAttach > 0 && attachDocuments)
        ? (forceRefreshDocuments
            ? (t('FinsiteFixDocuments') || 'Fix & re-save documents')
            : (t('FinsiteSaveDocuments') || 'Save documents'))
        : t('ConfirmImport');

    const handleConfirmClick = () => {
        if (!canConfirm) return;
        onConfirm?.({
            attachDocumentsForExisting: isFinsite
                ? (attachDocuments || forceRefreshDocuments)
                : true,
            forceRefreshDocuments: isFinsite ? forceRefreshDocuments : false,
        });
    };

    const renderTransactionsTable = (rows, { showSkipReason = false } = {}) => (
        <XtramTable height="medium">
            <Table stickyHeader size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>{t('Date')}</TableCell>
                        <TableCell>{t('Description')}</TableCell>
                        <TableCell>{t('Reference')}</TableCell>
                        <TableCell>{t('Amount')}</TableCell>
                        {showSkipReason && <TableCell>{t('Reason') || t('SkipReason') || 'Reason'}</TableCell>}
                        {showSkipReason && isFinsite && <TableCell>{t('Document') || 'Document'}</TableCell>}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows && rows.length > 0 ? (
                        rows.map((transaction, index) => (
                            <TableRow
                                key={`${transaction.finsiteEntryId || transaction.reference || index}-${index}`}
                                hover
                                className={showSkipReason ? xtramStyles.rowWarning : undefined}
                            >
                                <TableCell>{formatDate(transaction.transactionDate)}</TableCell>
                                <TableCell className={xtramStyles.wrapCell}>{transaction.description || '-'}</TableCell>
                                <TableCell>{transaction.reference || '-'}</TableCell>
                                <TableCell
                                    className={`${xtramStyles.numCell} ${transaction.amount >= 0 ? xtramStyles.textSuccess : xtramStyles.textError}`}
                                >
                                    {formatCurrency(transaction.amount)}
                                </TableCell>
                                {showSkipReason && (
                                    <TableCell className={xtramStyles.textWarning}>{skipReasonLabel(transaction.skipReason)}</TableCell>
                                )}
                                {showSkipReason && isFinsite && (
                                    <TableCell>
                                        {transaction.canAttachDocument ? (
                                            <XtramBadge tone="success">{t('FinsiteChequeCanAttach') || 'Check image available'}</XtramBadge>
                                        ) : transaction.hasChequeImage ? (
                                            <XtramBadge>{t('FinsiteChequeAlreadySaved') || 'Document already saved'}</XtramBadge>
                                        ) : '—'}
                                    </TableCell>
                                )}
                            </TableRow>
                        ))
                    ) : (
                        <TableRow>
                            <TableCell colSpan={showSkipReason ? (isFinsite ? 6 : 5) : 4} className={xtramStyles.emptyState}>
                                {showSkipReason ? (t('NoDuplicatesFound') || t('NoTransactions')) : t('NoTransactions')}
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </XtramTable>
    );

    const hasBankBalance = previewData?.bankBalance !== null && previewData?.bankBalance !== undefined;
    const balanceMismatch = hasBankBalance
        && previewData.balanceAfter !== null
        && Math.abs(previewData.balanceAfter - previewData.bankBalance) > 0.01;
    const duplicateCount = previewData?.duplicateCount || duplicateTransactions.length || 0;
    const title = `${t('Preview')} - ${isFinsite ? t('FetchFromFinsite') : t('ImportFile')}`;

    return (
        <XtramModal
            open={open}
            onClose={onClose}
            title={title}
            direction={direction}
            wide
            busy={loading}
            tabs={previewData && !loading && hasDuplicateTab ? [
                { key: 0, label: t('PreviewNewTransactions') || 'New', count: newTransactions.length },
                { key: 1, label: t('Duplicates') || 'Duplicates', count: duplicateTransactions.length, alert: duplicateTransactions.length > 0 },
            ] : undefined}
            activeTab={tab}
            onTabChange={setTab}
            footer={
                <>
                    <XtramButton variant="secondary" onClick={onClose}>{t('Cancel')}</XtramButton>
                    <XtramButton onClick={handleConfirmClick} disabled={!canConfirm}>{confirmLabel}</XtramButton>
                </>
            }
        >
            {loading ? (
                <div className={xtramStyles.loading}><CircularProgress size={28} /></div>
            ) : !previewData ? (
                <p className={xtramStyles.emptyState}>{t('NoPreviewData')}</p>
            ) : (
                <>
                    <div className={xtramStyles.section}>
                        <div className={xtramStyles.toolbarGroup}>
                            <XtramBadge tone="info">{`${t('PreviewNewTransactions') || 'New'}: ${newCount}`}</XtramBadge>
                            {hasDuplicateTab && (
                                <XtramBadge tone="warning">{`${t('Duplicates') || 'Duplicates'}: ${duplicateCount}`}</XtramBadge>
                            )}
                            {isFinsite && documentsAvailableToAttach > 0 && (
                                <XtramBadge tone="success">
                                    {`${t('FinsiteDocumentsAvailable') || 'Documents'}: ${documentsAvailableToAttach}`}
                                </XtramBadge>
                            )}
                            {isFinsite && documentsAvailableToRefresh > 0 && (
                                <XtramBadge tone="success">
                                    {`${t('FinsiteDocumentsRefreshable') || 'Can fix'}: ${documentsAvailableToRefresh}`}
                                </XtramBadge>
                            )}
                        </div>
                        <div className={xtramStyles.stats}>
                            <XtramStat label={t('BalanceBefore')} value={formatCurrency(previewData.balanceBefore)} />
                            <XtramStat label={t('BalanceAfter')} value={formatCurrency(previewData.balanceAfter)} />
                            <XtramStat
                                label={t('BalanceChange')}
                                tone={isPositive ? 'success' : 'error'}
                                value={(
                                    <>
                                        {isPositive ? <TrendingUpIcon fontSize="small" /> : <TrendingDownIcon fontSize="small" />}
                                        {formatCurrency(balanceChange)}
                                    </>
                                )}
                            />
                            {hasBankBalance && (
                                <>
                                    <XtramStat label={t('BankBalance')} value={formatCurrency(previewData.bankBalance)} />
                                    <XtramStat label={t('SystemBalance')} value={formatCurrency(previewData.systemBalance)} />
                                </>
                            )}
                        </div>
                        {balanceMismatch && (
                            <XtramNotice type="error">
                                {t('BalanceMismatch')}: {formatCurrency(Math.abs(previewData.balanceAfter - previewData.bankBalance))}
                            </XtramNotice>
                        )}
                    </div>

                    <div className={xtramStyles.section}>
                        {hasDuplicateTab ? (
                            <>
                                {tab === 1 && (
                                    <p className={xtramStyles.muted}>
                                        {t('FinsiteDuplicatesTabHelp')
                                            || 'These rows already exist (Excel/manual or previous Finsite fetch) and will be skipped on confirm.'}
                                    </p>
                                )}
                                {renderTransactionsTable(activeRows, { showSkipReason: tab === 1 })}
                            </>
                        ) : (
                            <>
                                <h3 className={xtramStyles.sectionTitle}>{t('Transactions')}</h3>
                                {renderTransactionsTable(newTransactions)}
                            </>
                        )}
                    </div>

                    {isFinsite && (documentsAvailableToAttach > 0 || documentsAvailableToRefresh > 0) && (
                        <div className={xtramStyles.section}>
                            {documentsAvailableToAttach > 0 && (
                                <XtramCheckbox
                                    checked={attachDocuments}
                                    onChange={setAttachDocuments}
                                    disabled={forceRefreshDocuments}
                                    label={t('FinsiteAttachDocumentsOption')
                                        || `Save check images for existing transactions (${documentsAvailableToAttach})`}
                                />
                            )}
                            {documentsAvailableToRefresh > 0 && (
                                <XtramCheckbox
                                    checked={forceRefreshDocuments}
                                    onChange={(on) => {
                                        setForceRefreshDocuments(on);
                                        if (on) setAttachDocuments(true);
                                    }}
                                    label={t('FinsiteForceRefreshDocumentsOption')
                                        || `Re-fetch check images and save under the correct path (${documentsAvailableToRefresh})`}
                                />
                            )}
                        </div>
                    )}
                </>
            )}
        </XtramModal>
    );
};

export default ImportPreviewModal;
